import { useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { coreSplitApis } from 'apis';
import { useLazyGetCurrentLoginInformationsQuery, useLazyGetCurrentUserDetailsQuery } from 'apis/user.api';
import {
  useBackFromImpersonatedAuthenticateMutation,
  useBackToImpersonatorMutation,
  useImpersonatedAuthenticateMutation,
  useImpersonateMutation,
} from 'apis/users.api';
import { ROUTES, SIDEBAR_ADMIN_LINKS, SIDEBAR_TENANT_LINKS } from 'constants/routes';
import { ImpersonatedAuthenticateResult, ImpersonateUserPayload } from 'interfaces/impersonate-user';
import { LinkItem, SubmenuItem } from 'interfaces/routes.interfaces';
import { CommonError } from 'interfaces/shared.interface';
import { getAuthState } from 'modules/auth/action';
import { useAppDispatch } from 'modules/store';
import { setAccessTokenToCookie } from 'utils/auth';

import { useUserConfig } from './useUserConfig';

interface Props {
  onError: VoidFunction;
}

const useImpersonateUser = ({ onError }: Props) => {
  const [isFetching, setIsFetching] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { handleGetUserConfig } = useUserConfig();
  const [fetchUserDetails] = useLazyGetCurrentUserDetailsQuery();
  const [fetchCurrentUserLoginInfo] = useLazyGetCurrentLoginInformationsQuery();

  const [impersonate, { error }] = useImpersonateMutation();
  const [impersonatedAuthenticate, { error: impersonatedAuthenticateError }] = useImpersonatedAuthenticateMutation();

  const [backToImpersonator, { error: backToImpersonatorError }] = useBackToImpersonatorMutation();
  const [backFromImpersonatedAuthenticate, { error: backFromImpersonatedAuthError }] =
    useBackFromImpersonatedAuthenticateMutation();

  const { error: impersonateError } = (error as CommonError)?.data || {};
  const { error: impersonatedAuthError } = (impersonatedAuthenticateError as CommonError)?.data || {};
  const { error: backImpersonatorError } = (backToImpersonatorError as CommonError)?.data || {};
  const { error: backImpersonatorAuthError } = (backFromImpersonatedAuthError as CommonError)?.data || {};

  const impersonateUserError =
    impersonateError || impersonatedAuthError || backImpersonatorError || backImpersonatorAuthError;

  const flattenLinks = (links: (LinkItem | SubmenuItem)[]): string[] => {
    const result: string[] = [];
    links.forEach((link) => {
      if ('submenu' in link && link.submenu) {
        result.push(...flattenLinks(link.submenu));
      } else if ('path' in link && link.path) {
        result.push(link.path);
      }
    });
    return result;
  };

  const allRoutes = useMemo(() => {
    const adminLinks = flattenLinks(SIDEBAR_ADMIN_LINKS);
    const tenantLinks = flattenLinks(SIDEBAR_TENANT_LINKS);
    return [...adminLinks, ...tenantLinks];
  }, []);

  const matchDynamicRoute = (route: string, path: string): boolean => {
    const routeParts = route.split('/').filter(Boolean);
    const pathParts = path.split('/').filter(Boolean);
    if (routeParts.length !== pathParts.length) {
      return false;
    }
    return routeParts.every((part, index) => (part.startsWith(':') ? true : part === pathParts[index]));
  };

  const handleUserInfo = async (authData: ImpersonatedAuthenticateResult) => {
    const { accessToken, encryptedAccessToken, storybookAuthToken, expireInSeconds } = authData;

    setAccessTokenToCookie(accessToken, encryptedAccessToken, storybookAuthToken, expireInSeconds);

    if (coreSplitApis.util) {
      dispatch(coreSplitApis.util?.resetApiState());
    }

    dispatch(getAuthState());

    await handleGetUserConfig();
    await fetchCurrentUserLoginInfo();
    await fetchUserDetails();
  };

  // If there is a payload, impersonate the user, otherwise return to the impersonator
  const handleImpersonator = async (payload?: ImpersonateUserPayload) => {
    setIsFetching(true);

    try {
      const { result } = await (payload ? impersonate(payload) : backToImpersonator()).unwrap();

      const response = await (payload
        ? impersonatedAuthenticate(result?.impersonationToken)
        : backFromImpersonatedAuthenticate(result?.impersonationToken)
      ).unwrap();

      await handleUserInfo(response?.result);

      const currentPath = location.pathname;

      const routeExists = allRoutes.some((route) =>
        route.includes(':') ? matchDynamicRoute(route, currentPath) : route === currentPath
      );

      if (!routeExists || ROUTES.notFound) {
        navigate(ROUTES.dashboard);
      }

      setIsFetching(false);
    } catch (e) {
      setIsFetching(false);
      onError();
      console.error(e);
    }
  };

  return {
    handleImpersonator,
    isFetching,
    error: impersonateUserError,
  };
};

export default useImpersonateUser;
